import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { ExcelSettings } from 'app/model/dataflow/dataset-settings/excel-settings';
import { BooleanDataFlowType, IntegerDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import { FileBasedSettingsAdapter } from './file-based-settings.adapter';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(ExcelSettings)
})
export class ExcelFormatAdapter extends FileBasedSettingsAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(
        dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean,
        isInline?: boolean, datasetSettings?: DataFlowDatasetSettings, _format?: string, wildcardPaths?: DataFlowExpression[]
    ): void {
        if (isInline || !dataset) {
            return;
        }

        props.push(`format: '${DatasetFormatType.Excel}'`);

        DataFlowAdapterUtils.addCommonPropsForFileBasedFormats(dataset, linkedService, props, isSource, isInline, datasetSettings, wildcardPaths);

        const sheetName = getAttributeValueFromDataset<string>(dataset, 'sheetName');
        if (sheetName) {
            props.push(`sheetName: ${DataFlowAdapterUtils.formatExpressionValue(sheetName)}`);
        }

        const sheetIndex = getAttributeValueFromDataset<number | string | null>(dataset, 'sheetIndex');
        if (sheetIndex !== null && sheetIndex !== undefined && !sheetName) {
            const expr = new DataFlowExpression();
            expr.expression = String(sheetIndex);
            props.push(`sheetIndex: ${DataFlowExpressionUtils.wrapExpressionConfigValue(expr, IntegerDataFlowType.TYPE)}`);
        }

        const range = getAttributeValueFromDataset(dataset, 'range');
        if (range) {
            props.push(`range: ${DataFlowAdapterUtils.formatExpressionValue(range)}`);
        }

        const nullValue = getAttributeValueFromDataset(dataset, 'nullValue');
        if (nullValue) {
            props.push(`nullValue: ${DataFlowAdapterUtils.formatExpressionValue(nullValue)}`);
        }

        const compressionCodec = getAttributeValueFromDataset(dataset, 'compressionCodec');
        if (compressionCodec) {
            props.push(`compressionType: '${compressionCodec}'`);
            const compressionLevel = getAttributeValueFromDataset(dataset, 'compressionLevel');
            if (compressionLevel) {
                props.push(`compressionLevel: ${DataFlowAdapterUtils.formatExpressionValue(compressionLevel)}`);
            }
        }

        const firstRowAsHeader = getAttributeValueFromDataset<boolean | string | null>(dataset, 'firstRowAsHeader');
        if (firstRowAsHeader !== null && firstRowAsHeader !== undefined) {
            const expr = new DataFlowExpression();
            expr.expression = String(firstRowAsHeader);
            props.push(`firstRowAsHeader: ${DataFlowExpressionUtils.wrapExpressionConfigValue(expr, BooleanDataFlowType.TYPE)}`);
        }
    }

    protected async toSourceDSL(excelSettings: ExcelSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSourceDSL(excelSettings)));
        if (excelSettings.compressionType) {
            props.push(`compressionType: '${excelSettings.compressionType}'`);
            props.push(
                `compressionLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(excelSettings.compressionLevel)}`
            );
        }
        if (!excelSettings.useSheetName) {
            props.push(
                `sheetIndex: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    excelSettings.sheetIndex,
                    IntegerDataFlowType.TYPE
                )}`
            );
        } else {
            props.push(`sheetName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(excelSettings.sheetName)}`);
        }
        if (excelSettings.range.expression) {
            props.push(`range: ${DataFlowExpressionUtils.wrapExpressionConfigValue(excelSettings.range)}`);
        }
        if (excelSettings.nullValue.expression) {
            props.push(`nullValue: ${DataFlowExpressionUtils.wrapExpressionConfigValue(excelSettings.nullValue)}`);
        }
        props.push(
            `firstRowAsHeader: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                excelSettings.firstRowAsHeader,
                BooleanDataFlowType.TYPE
            )}`
        );
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<ExcelSettings> {
        const excelSettings = (await super.toSourceModel(owner, configs, handledConfigs)) as ExcelSettings;
        if (configs.has('compressionType')) {
            excelSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('compressionType')
            );
            handledConfigs.push('compressionType');
        }
        if (configs.has('compressionLevel')) {
            excelSettings.compressionLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('compressionLevel'),
                owner
            );
            handledConfigs.push('compressionLevel');
        }
        if (configs.has('sheetName')) {
            excelSettings.useSheetName = true;
            excelSettings.sheetName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('sheetName'),
                owner
            );
            handledConfigs.push('sheetName');
        } else if (configs.has('sheetIndex')) {
            excelSettings.useSheetName = false;
            excelSettings.sheetIndex = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('sheetIndex'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('sheetIndex');
        }
        if (configs.has('range')) {
            excelSettings.range = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('range'), owner);
            handledConfigs.push('range');
        }
        if (configs.has('firstRowAsHeader')) {
            excelSettings.firstRowAsHeader = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('firstRowAsHeader'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('firstRowAsHeader');
        }
        if (configs.has('nullValue')) {
            excelSettings.nullValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('nullValue'),
                owner
            );
            handledConfigs.push('nullValue');
        }
        return excelSettings;
    }

    protected _generateSetting(owner: DataFlowSource): ExcelSettings {
        return Owned.create<ExcelSettings>(ExcelSettings.MODEL, owner);
    }
}
